import { Award, Handshake, HandshakeIcon, Star, Trophy } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';

import Faqs from './faq';

import IcBoot from '@/assets/ic_boot.svg';
import { Button, InputSearch, Loader, NavBar, Tabs } from '@/components';
import { AdSlot } from '@/components/GoogleAds/AdSlot';
import { TshirtTeam } from '@/components/TshirtTeam';
import useAuth from '@/hooks/useAuth';
import { useGoogleAds } from '@/hooks/useGoogleAds';
import useTshirtAndShield from '@/hooks/useTshirtAndShield';
import ROUTES from '@/router/routes';
import {
  GetTop50Medals,
  initMedallero,
  searchMedallero,
} from '@/services/medals';
import { ResponseTop50Medals } from '@/types/medals';
import { formatNumberToLocale } from '@/utils';
import { ADS_DIV_ID, SECTION_NAME_FOR_ADS } from '@/utils/ads';

const Icons = {
  top: <Trophy className="text-gdt-status-orange" size={20} />,
  challenge: <Handshake className="text-gdt-blue" size={20} />,
  premium: <Star className="text-gdt-status-yellow" size={20} />,
  award: <Award className="text-gdt-blue" size={20} />,
  f5: <img alt="f5" className="w-5 h-5" src={IcBoot} />,
  handshake: <HandshakeIcon className="text-gdt-blue" size={20} />,
};

interface MedalsData {
  icon: JSX.Element;
  iconBgColor: string;
  title: string;
  date: string;
  medals: number;
  torneo?: string;
}

interface Top50MedalsData {
  user: string;
  location: string;
  team: string;
  medals: number;
  index: number;
}

interface MedalsProps {
  contentCurrentPage: MedalsData[];
  allMedals: MedalsData[];
  handleContentCurrentPage: (medals: MedalsData[]) => void;
}

const Medals = ({ contentCurrentPage, allMedals }: MedalsProps) => {
  useGoogleAds({ adsSections: SECTION_NAME_FOR_ADS.medallero });

  if (contentCurrentPage.length === 0) {
    return (
      <div className="bg-white rounded-lg py-10 justify-center items-center flex flex-col">
        <p className="text-lg font-semibold text-gdt-gray-2 px-9 ">
          Empezás a sumar medallas en la primera fecha de Gran DT.
        </p>
        <Link
          className="text-gdt-blue font-bold text-lg underline cursor-pointer"
          id="linkFaq"
          rel="noopener noreferrer"
          target="_blank"
          to={`${ROUTES.preguntasFrecuentes}?selectedScreen=TeamBuildingAndAwards&accordionId=juego-como-sumo-medallas`}
        >
          Consultá qué logros tienen recompensa
        </Link>
      </div>
    );
  }
  return (
    <>
      <div className="bg-white rounded-lg">
        {allMedals.map((medal, index) => (
          <div
            key={index}
            className="flex justify-between items-center border-b border-gdt-gray-5 py-8 px-10"
          >
            {' '}
            <div className="flex gap-5">
              <div
                className={`${medal.iconBgColor} rounded-full h-11 w-11 flex-shrink-0 flex items-center justify-center`}
              >
                {medal.icon}
              </div>
              <div className="flex flex-col items-start flex-1 min-w-0">
                <span className="font-bold text-base break-words">
                  {medal.title}
                  {medal.torneo ? ` de ${medal.torneo}` : ''}
                </span>
                <span className="text-sm font-semibold text-gdt-gray-4">
                  {medal.date}
                </span>
              </div>
            </div>
            <div className="flex items-center mx-8">
              <span className="text-base font-semibold text-gdt-gray-2">
                {medal.torneo ? medal.medals : '--'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

const MyMedalsMobile = () => {
  const [allMedals, setAllMedals] = useState<MedalsData[]>([]);
  const [contentCurrentPage, setContentCurrentPage] = useState<MedalsData[]>(
    []
  );
  const [allMedalsTop50, setAllMedalsTop50] = useState<Top50MedalsData[]>([]);
  const [top50Data, setTop50Data] = useState<Top50MedalsData[]>([]);
  const [userData, setUserData] = useState({
    posicion: 0,
    nombreEquipo: '',
    nombreUsuario: '',
    cantidad: 0,
    rankingPublicado: 0,
  });
  const [searchUser, setSearchUser] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const { userInitialData } = useAuth();

  const { data: dataTshirtShield } = useTshirtAndShield();

  const iconResolver = ({
    type,
    tourney,
    msg,
  }: {
    type?: string;
    tourney?: string;
    msg?: string;
  }) => {
    if (
      tourney?.includes('Trivia') ||
      type?.includes('Trivia') ||
      msg?.includes('Trivia')
    ) {
      return {
        icon: Icons.award,
        iconBgColor: 'bg-gdt-gray-6',
      };
    }
    if (
      tourney?.includes('Premium') ||
      type?.includes('Premium') ||
      msg?.includes('Premium')
    ) {
      return {
        icon: Icons.premium,
        iconBgColor: 'bg-gdt-status-yellow-light',
      };
    }
    if (
      tourney?.includes('Primera División') ||
      type?.includes('Primera División') ||
      msg?.includes('Primera División')
    ) {
      return {
        icon: Icons.top,
        iconBgColor: 'bg-gdt-status-orange-light',
      };
    }
    if (
      tourney?.includes('Fútbol 5') ||
      type?.includes('Fútbol 5') ||
      msg?.includes('Fútbol 5')
    ) {
      return {
        icon: Icons.f5,
        iconBgColor: 'bg-gdt-gray-6',
      };
    }

    if (
      tourney?.includes('Desafios') ||
      type?.includes('Desafios') ||
      msg?.includes('Desafios')
    ) {
      return {
        icon: Icons.handshake,
        iconBgColor: 'bg-gdt-status-blue-light',
      };
    }
  };

  const { isLoading } = useQuery('initMedallero', async () => initMedallero(), {
    onSuccess(data) {
      const medals = (data.medallero?.objectList ?? []).map((medal: any) => ({
        icon:
          iconResolver({
            type: medal.tipoMedalla,
            tourney: medal.torneo,
            msg: medal.txtMensaje,
          })?.icon ?? Icons.top,
        iconBgColor:
          iconResolver({
            type: medal.tipoMedalla ?? medal.txtMensaje,
            tourney: medal.torneo,
            msg: medal.txtMensaje,
          })?.iconBgColor ?? 'bg-gdt-status-orange-light',
        title:
          medal.tipoMedalla ??
          medal.torneo ??
          medal.txtMessage ??
          medal.txtMensaje,
        date: medal.fecha,
        medals: medal.cantidadMedallas,
        torneo: medal.torneo,
      }));
      setAllMedals(medals);
      setContentCurrentPage(medals.slice(0, 10));

      setUserData({
        posicion: data.posicion,
        nombreEquipo: userInitialData?.nombre_equipo ?? '',
        nombreUsuario: data.nombreUsuario,
        cantidad: data.cantidad,
        rankingPublicado: data.rankingPublicado ?? 0,
      });
      if (data.isExportado) {
        GetTop50Medals(String(data.rankingPublicado)).then(async (data) => {
          const top = await data;
          const top50 = top.map((user: any) => ({
            user: `${user.nombre} ${user.apellido}`,
            location: user.provincia,
            team: user.nombreEquipo,
            medals: user.cantidadMedallas,
            index: user.posicion,
          }));
          setAllMedalsTop50(top50);
          setTop50Data(top50);
        });
      }
    },
  });

  const handleContentCurrentPage = useCallback((medals: MedalsData[]) => {
    setContentCurrentPage(medals);
  }, []);

  const handleSearchUser = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // hasta 30 caracteres, solo letras y espacios
    if (e.target.value.length > 30) {
      return;
    }
    const rawValue = e.target.value;
    const lettersOnly = rawValue
      .replace(/[^a-záéíóúüñA-ZÁÉÍÓÚÜÑ\s]/g, '')
      .toLowerCase();

    setSearchUser(lettersOnly);

    if (lettersOnly === '') {
      setAllMedalsTop50(top50Data);
      return;
    }

    if (lettersOnly !== '' && lettersOnly.length < 3) {
      setAllMedalsTop50([]);
      return;
    }

    try {
      const results = await searchMedallero(lettersOnly);
      const formattedResults = results.usuarios.map(
        (user: ResponseTop50Medals) => ({
          user: `${user.nombre} ${user.apellido}`,
          location: user.provincia ?? '',
          team: user.nombreEquipo,
          medals: user.cantidadMedallas,
          index: user.posicion,
        })
      );
      setAllMedalsTop50(formattedResults);
    } catch (error) {
      console.error('Error searching medallero:', error);
      setAllMedalsTop50([]);
    }
  };

  const [showFaqs, setShowFaqs] = useState(false);
  const [faqPageToShow, setFaqPageToShow] = useState(0);

  const handleExitFaqs = () => {
    setShowFaqs(false);
  };

  if (showFaqs) {
    return (
      <Faqs handleBackButton={handleExitFaqs} pageToShow={faqPageToShow} />
    );
  }

  const firstDate = userInitialData?.fechas_disponibles_obj?.[0]?.nombre;

  return (
    <NavBar
      mobile={{
        leftContent: {
          showMenuAction: true,
          items: true,
        },
        centerContent: {
          title: 'Mis Medallas',
        },
      }}
    >
      <div className="mx-6">
        <Tabs
          activeTab={selectedTab}
          className="mt-4 "
          tabs={[{ name: 'Mis Medallas' }, { name: 'Top 50 Medallero' }]}
          variant="primary"
          onClick={(e) => setSelectedTab(e)}
        />
      </div>

      <div className="flex justify-center mt-4 mx-6 ">
        <AdSlot divId={ADS_DIV_ID.horizontal} variant="HORIZONTAL" />
      </div>

      <div className="container mx-auto mb-20">
        <div className="m-6 md:m-0">
          <p className=" text-sm text-gdt-gray-2 mb-10">
            Cuanto mejor sea tu rendimiento, más medallas ganás y más premios te
            llevás.{' '}
          </p>
        </div>
        {selectedTab === 0 ? (
          <>
            <div className="mx-6 flex flex-col">
              <div className="bg-white  rounded-t-lg p-6 flex gap-12">
                <div className="w-16 min-w-16">
                  <TshirtTeam
                    shield={dataTshirtShield?.shield}
                    tshirt={dataTshirtShield?.tshirt}
                    withShield={true}
                  />
                </div>
                <div className="flex justify-between w-full items-start">
                  <div className="flex flex-col">
                    <span className="text-lg font-bold">
                      {userData.nombreEquipo}
                    </span>
                    <span className="text-base font-medium text-gdt-gray-3">
                      {userData.nombreUsuario}
                    </span>
                  </div>
                  <div className="flex flex-col gap-x-3">
                    <span className="text-base font-semibold text-gdt-gray-2">
                      Medallas
                    </span>
                    <span className="text-2xl text-gdt-blue">
                      {formatNumberToLocale(userData.cantidad)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white  rounded-b-lg border-t border-gdt-gray-5 flex px-6 py-4 gap-4 items-center">
                <div className="flex items-center gap-2">
                  <div className="bg-gdt-status-yellow-light w-9 h-9 rounded-full flex justify-center items-center">
                    <Award className="text-gdt-status-yellow" size={20} />
                  </div>
                  <span className="text-xs text-gdt-gray-2 font-semibold">
                    TU POSICIÓN EN EL MEDALLERO
                  </span>
                </div>
                <span className="font-bold text-xl text-black">
                  {[0, '-'].includes(userData.posicion)
                    ? '-'
                    : formatNumberToLocale(userData.posicion)}
                </span>
              </div>
            </div>
            <div className="flex justify-between px-10 mb-3 mt-10">
              <span className="font-semibold text-gdt-gray-4">Tus logros</span>
              <span className="font-semibold text-gdt-gray-4">Medallas</span>
            </div>
            {isLoading ? (
              <Loader />
            ) : (
              <Medals
                allMedals={allMedals}
                contentCurrentPage={contentCurrentPage}
                handleContentCurrentPage={handleContentCurrentPage}
              />
            )}
          </>
        ) : (
          <>
            <div className="px-10 py-7">
              <InputSearch
                handleCancel={() => {
                  setSearchUser('');
                  setAllMedalsTop50(top50Data);
                }}
                name="searchQuery"
                placeholder="Buscar DT por nombre y apellido"
                value={searchUser}
                onChange={handleSearchUser}
              />
            </div>
            <div className="flex justify-between px-8 mb-3 font-semibold text-gdt-gray-4">
              <div className="flex gap-5">
                <span>Pos</span>
                <span>DT</span>
              </div>
              <div className="flex ">
                <span>Medallas</span>
              </div>
            </div>
            <div className="bg-white rounded-lg">
              {searchUser.length > 0 && allMedalsTop50.length === 0 && (
                <div className="flex justify-center items-center h-40">
                  <span className="text-lg font-semibold text-gdt-gray-2">
                    No se han encontrado resultados
                  </span>
                </div>
              )}
              {allMedalsTop50.length === 0 && searchUser.length === 0 && (
                <div className="flex justify-center items-center h-40">
                  {userData.rankingPublicado === 0 ? (
                    <span className="text-lg font-semibold text-gdt-gray-2">
                      Consultá el ranking a partir de la{' '}
                      {firstDate || userInitialData?.fechaActiva.nombre}
                    </span>
                  ) : (
                    <span className="text-lg font-semibold text-gdt-gray-2">
                      Muy pronto podrás conocer el Top 50 del Medallero
                    </span>
                  )}
                </div>
              )}
              {allMedalsTop50.map((user, index) => (
                <div
                  key={user.index}
                  className={`flex justify-between items-center border-b border-gdt-gray-5 py-8 px-10`}
                >
                  <div className="flex gap-5">
                    <div
                      className={`rounded-full h-11 w-11 flex items-center font-bold justify-center ${
                        index === 0
                          ? 'bg-gdt-blue text-white'
                          : 'bg-gdt-blue-light2 text-gdt-blue'
                      }`}
                    >
                      {formatNumberToLocale(user.index)}
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="font-bold text-base">{user.user}</span>
                      <span className="text-base col-span-3 font-semibold text-gdt-gray-2 flex justify-start">
                        {user.team}
                      </span>
                      <span className="text-sm font-semibold text-gdt-gray-4">
                        {user.location}
                      </span>
                    </div>
                  </div>
                  <div className="gap-8 ">
                    <span className="text-base col-span-1 font-semibold text-gdt-gray-2 flex justify-end">
                      {user.medals}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="flex justify-center gap-10 mt-4">
          <Button
            className="font-bold"
            type="button"
            variant="text"
            onClick={() => {
              setFaqPageToShow(0);
              setShowFaqs(true);
            }}
          >
            ¿Cómo sumo medallas?
          </Button>
          <Button
            className="font-bold"
            type="button"
            variant="text"
            onClick={() => {
              setFaqPageToShow(1);
              setShowFaqs(true);
            }}
          >
            Premios
          </Button>
        </div>
      </div>

      <AdSlot className="mx-auto mb-8" divId={ADS_DIV_ID.caja1} variant="BOX" />
    </NavBar>
  );
};

export default MyMedalsMobile;
